import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface SearchResult {
  id: string;
  type: 'product' | 'customer' | 'sale' | 'user' | 'transfer' | 'invoice';
  title: string;
  subtitle?: string;
  description?: string;
  metadata?: any;
  relevance: number;
}

export interface SearchResponse {
  results: SearchResult[];
  query: string;
  total: number;
}

export const useGlobalSearch = () => {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [query, setQuery] = useState('');
  const { selectedCompany } = useAuth();
  const { toast } = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);

  const search = useCallback(async (searchQuery: string) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults([]);
      setQuery('');
      return;
    }

    if (!selectedCompany) {
      toast({
        title: "Error",
        description: "No hay empresa seleccionada para buscar",
        variant: "destructive",
      });
      return;
    }

    setIsSearching(true);
    setQuery(searchQuery);
    
    // Create new abort controller
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const { data, error } = await supabase.functions.invoke('global-search', {
        body: {
          query: searchQuery.trim(),
          company_id: selectedCompany.id,
          limit: 20
        }
      });

      if (error) {
        throw error;
      }

      if (data && !controller.signal.aborted) {
        setResults(data.results || []);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) {
        console.error('Search error:', error);
        toast({
          title: "Error de búsqueda",
          description: "No se pudo realizar la búsqueda. Inténtalo de nuevo.",
          variant: "destructive",
        });
        setResults([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsSearching(false);
      }
    }
  }, [selectedCompany, toast]);

  const clearSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setResults([]);
    setQuery('');
    setIsSearching(false);
  }, []);

  return {
    results,
    isSearching,
    query,
    search,
    clearSearch
  };
};